/* eslint-disable react/prop-types */
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useRecoilState } from 'recoil';
import { find, orderBy } from 'lodash';
import { Spinner, ErrorMessage } from '@gsa/afp-component-library';
import {
  GET_SOLICITATION_BY_ID,
  GET_FVS_OPTIONS_FOR_YEAR,
  SUBMIT_SOLICITATION_AS_NEW,
} from '../../queries';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';
import {
  solicitationNoOpenPeriodErrorNotifications,
  solicitationNoSinErrorNotifications,
} from '../../atoms/solicitaion-submit-error-atom';
import { pageErrorNotifications } from '../../../../utilities/error-handling';

// Submit-time validation codes the API returns in place of a message, and the
// fields each one puts into an error state.
const SUBMIT_VALIDATION_CODES = {
  100001: { openPeriod: true }, // no open period on the solicitation
  100002: { noSin: true }, // no SIN associated with the solicitation
  100003: { openPeriod: true, noSin: true }, // neither
};

const FIELD_ERRORS_GUIDANCE = (
  <div>
    <div className="text-bold">This page contains errors</div>Please address all
    fields outlined in red before proceeding to the next step.
  </div>
);

const ReviewSubmitContext = createContext({});

const ReviewSubmitProvider = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const emDashUnicode = '\u2014';
  const [solicitation, setSolicitation] = useState(null);
  const [solicitationID, setSolicitationID] = useState(0);
  const [SIcount, setSIcount] = useState(0);
  const [standardItems, setStandardItems] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [, setSolicitationNotification] = useRecoilState(
    solicitationNotifications,
  );
  const [noOpenPeriodErrnotifications, setnoOpenPeriodErrnotification] =
    useRecoilState(solicitationNoOpenPeriodErrorNotifications);
  const [noSinErrnotifications, setnoSinErrnotification] = useRecoilState(
    solicitationNoSinErrorNotifications,
  );

  useEffect(() => {
    // on component unmount clear error messages
    return () => {
      setnoOpenPeriodErrnotification([{}]);
      setnoSinErrnotification([{}]);
    };
  }, []);

  const setOpenPeriodErrorNotification = useCallback(() => {
    setnoOpenPeriodErrnotification([
      {
        id: 'SUBMIT_SOLICITATION_AS_NEW_NO_OPEN_PEROD',
        message: 'Add an open period to submit the solicitation.',
        type: 'error',
        closeable: false,
        showInModal: false,
      },
      noOpenPeriodErrnotifications,
    ]);
  }, [noOpenPeriodErrnotifications, setnoOpenPeriodErrnotification]);

  const setNoSinErrorNotification = useCallback(() => {
    setnoSinErrnotification([
      {
        id: 'SUBMIT_SOLICITATION_AS_NEW_NO_SIN',
        message:
          'Select at least one Standard Item and assign quantities to submit the solicitation.',
        type: 'error',
        closeable: false,
        showInModal: false,
      },
      noSinErrnotifications,
    ]);
  }, [noSinErrnotifications, setnoSinErrnotification]);

  const getStandardItemCount = useCallback((programs) => {
    let count = 0;
    if (programs && programs.length > 0) {
      programs.forEach((program) => {
        count += program.solicitationLines?.length;
      });
    }
    return count;
  }, []);

  const getStandardItems = useCallback(
    (programs, vTypes) => {
      const stdItems = [];
      if (programs && programs.length > 0) {
        programs.forEach((program) => {
          program.solicitationLines?.forEach((solctnLine) => {
            const vehicleType = find(vTypes, {
              value: solctnLine.standardItem.vehicleType,
            });
            stdItems.push({
              standardItemNumber: solctnLine.standardItem.standardItemNumber,
              title: `${solctnLine.standardItem.standardItemNumber}-${solctnLine.standardItem.title}`,
              vehicleType: vehicleType ? vehicleType.label : emDashUnicode,
              estimatedQty: solctnLine.estimatedQty,
              estimatedFleetQty: solctnLine.estimatedFleetQty,
            });
          });
        });
      }
      return orderBy([...stdItems], 'standardItemNumber', 'asc');
    },
    [emDashUnicode],
  );

  const { loading: solicitationLoading, refetch } = useQuery(
    GET_SOLICITATION_BY_ID,
    {
      skip: !id,
      variables: { id: Number(id) },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const current = data.getSolicitationById;
        setSolicitation(current);
        setSolicitationID(current.id);
        const programs = current?.programs;
        setSIcount(getStandardItemCount(programs));
      },
    },
  );

  const [getFvsCodes] = useLazyQuery(GET_FVS_OPTIONS_FOR_YEAR, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const vtypes = data.getVehicleClassificationFVS.filter(
        (inst) => inst.uniqueKey === 'vehicleType',
      );
      setVehicleTypes(vtypes);
    },
  });

  const refetchSolicitation = useCallback(() => {
    return refetch();
  }, [refetch]);

  const [submitSolicitation] = useMutation(SUBMIT_SOLICITATION_AS_NEW, {
    onError: (requestError) => {
      const flagged = SUBMIT_VALIDATION_CODES[requestError.message];

      if (flagged?.openPeriod) setOpenPeriodErrorNotification();
      if (flagged?.noSin) setNoSinErrorNotification();

      // "address all fields outlined in red" only makes sense when a field was
      // actually put into an error state. Any other failure -- a system fault,
      // a permission problem -- is reported as itself rather than sending the
      // user looking for red outlines that are not there.
      setSolicitationNotification(
        pageErrorNotifications(requestError, {
          id: 'SUBMIT_SOLICITATION_AS_NEW',
          message: flagged ? FIELD_ERRORS_GUIDANCE : requestError.message,
          type: 'error',
          closeable: false,
          showInModal: false,
        }),
      );
    },
    onCompleted: (response) => {
      const { id: responseId } = response.submitSolicitation;
      if (responseId) {
        setSolicitationNotification([
          {
            id: 'SUBMIT_SOLICITATION_AS_NEW',
            message: `Solicitation ${solicitation.solicitationNumber} was successfully updated.`,
            type: 'success',
            closeable: true,
            showInModal: false,
            showOnHomePage: true,
          },
        ]);
        navigate(`/catalog/solicitations`);
      }
    },
  });

  useEffect(() => {
    if (solicitation?.programs?.length && vehicleTypes.length) {
      setStandardItems(getStandardItems(solicitation.programs, vehicleTypes));
    }
  }, [solicitation, vehicleTypes, getStandardItems]);

  const contextValue = useMemo(
    () => ({
      solicitation,
      solicitationID,
      solicitationLoading,
      SIcount,
      standardItems,
      vehicleTypes,
      getFvsCodes,
      refetchSolicitation,
      submitSolicitation,
    }),
    [
      solicitation,
      solicitationID,
      solicitationLoading,
      SIcount,
      standardItems,
      vehicleTypes,
      getFvsCodes,
      refetchSolicitation,
      submitSolicitation,
    ],
  );

  if (solicitationLoading) {
    return (
      <div className="margin-top-8">
        <Spinner />
      </div>
    );
  }

  if (id && !solicitation) {
    return <ErrorMessage>Solicitation not found</ErrorMessage>;
  }

  return (
    <ReviewSubmitContext.Provider value={contextValue}>
      {children}
    </ReviewSubmitContext.Provider>
  );
};

export default ReviewSubmitProvider;

export const useReviewSubmit = () => useContext(ReviewSubmitContext);
